import { Box, Stack, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import ScheduleCard from '/@/components/bayer/schedule-card';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { getAppInstance, getCornRecommendationEnabled } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { reverse } from '/@/utils/router';
import { managementRecommendationUrl } from '/@/views/recommendations/seed-recommendation-route';
import type { SectionAudience } from '../audience';

function SuggestionsSection({
    setOpenYieldEstimationModal,
    audience = 'growers',
}: {
    setOpenYieldEstimationModal: (value: boolean) => void;
    audience?: SectionAudience;
}) {
    const navigate = useNavigate();
    // The suggestion calendar, actions and labels are identical across audiences;
    // only the wording changes, so each card picks its phrasing rather than the
    // whole schedule being duplicated. Memoised on `audience` so it can be a stable
    // dependency of the suggestions memo below.
    const pick = useCallback(
        (growersCopy: string, ownFieldsCopy: string) => (audience === 'own-fields' ? ownFieldsCopy : growersCopy),
        [audience]
    );
    // Hoisted so the untyped `settings.app` access appears once, not per card.
    const appName = settings.app.name;
    const goToSeedRecommendation = () => {
        navigate(
            reverse('recommendations.RecommendationFieldSelect', {
                qs: { entrySource: 'rv-home-suggestions' },
                params: {
                    crop: 'corn',
                },
            })
        );
    };
    const goToCPRecommendation = ({ qs }: { qs?: any }) => {
        mpTrackEvent('Crop Protection Recommendation began', {
            entry_source: 'rv-home-suggestions',
        });
        navigate(reverse('cropProtectionRecommendation', { qs: qs }));
    };
    const goToManagementRecommendation = () => {
        navigate(managementRecommendationUrl({ entrySource: 'rv-home-suggestions' }));
    };
    const { i18n } = useTranslation();
    const appInstance = useSelector(getAppInstance);
    const cropProtectionEnabled = appInstance.enableCropProtectionRecommendation;
    const cornRecommendationEnabled = useSelector((state) => getCornRecommendationEnabled(state, appInstance));
    const suggestions = useMemo(
        () =>
            [
                {
                    cards: [
                        {
                            label: _('Siembra Temprana'),
                            title: pick(
                                _('Ayudá a los productores a estimar el rendimiento de su cosecha'),
                                _('Estimá el rendimiento de tu cosecha')
                            ),
                            description: pick(
                                _(
                                    'A través de la carga muestras, los productores pueden estimar el rendimiento y conocer la fecha probable de cosecha.'
                                ),
                                _(
                                    'A través de la carga de muestras podés estimar el rendimiento y conocer la fecha probable de cosecha.'
                                )
                            ),
                            actionText: _('Ver cómo calcular el rendimiento'),
                            action: () => {
                                setOpenYieldEstimationModal(true);
                            },
                        },
                        {
                            label: _('Siembra tardía'),
                            title: pick(
                                _('Acompañá el monitoreo del cultivo para detectar plagas y malezas'),
                                _('Monitoreá tus cultivos para detectar plagas y malezas')
                            ),
                            description: pick(
                                _(
                                    'Ayudá a los productores a detectar plagas y enfermedades a tiempo para proteger el rendimiento del cultivo.'
                                ),
                                _('Detectá plagas y enfermedades a tiempo para proteger el rendimiento del cultivo.')
                            ),
                            actionText: _('Recomendar herbicidas'),
                            action: () => {
                                goToCPRecommendation({ qs: { type: 'weeds' } });
                            },
                            hidden: !cropProtectionEnabled,
                        },
                    ],
                    fromMonth: 0, // Enero
                    toMonth: 0, // Enero
                },
                {
                    cards: [
                        {
                            label: _('Siembra Temprana'),
                            title: pick(
                                _('Ayudá a los productores a estimar el rendimiento de su cosecha'),
                                _('Estimá el rendimiento de tu cosecha')
                            ),
                            description: pick(
                                _(
                                    'A través de la carga muestras, los productores pueden estimar el rendimiento y conocer la fecha probable de cosecha.'
                                ),
                                _(
                                    'A través de la carga de muestras podés estimar el rendimiento y conocer la fecha probable de cosecha.'
                                )
                            ),
                            actionText: _('Ver cómo calcular el rendimiento'),
                            action: () => {
                                setOpenYieldEstimationModal(true);
                            },
                        },
                        {
                            label: _('Siembra tardía'),
                            title: pick(
                                _('Seguí de cerca el monitoreo de plagas y enfermedades de tus productores'),
                                _('Seguí de cerca el monitoreo de plagas y enfermedades de tus lotes')
                            ),
                            description: pick(
                                _(
                                    'Evaluá el estado del cultivo y recomendales los fungicidas e insecticidas adecuados.'
                                ),
                                _('Evaluá el estado del cultivo y elegí los fungicidas e insecticidas adecuados.')
                            ),
                            actionText: _('Recomendar fitosanitarios'),
                            action: () => {
                                goToCPRecommendation({});
                            },
                            hidden: !cropProtectionEnabled,
                        },
                    ],
                    fromMonth: 1, // Febrero
                    toMonth: 1, // Febrero
                },
                {
                    cards: [
                        {
                            label: _('Siembra Temprana'),
                            title: pick(
                                _('Incentivá a los productores a registrar los datos de cosecha'),
                                _('Registrá los datos de tu cosecha')
                            ),
                            description: pick(
                                _(
                                    'Al cargar la información, podrán acceder a un reporte detallado de la campaña con resultados clave para planificar la próxima.'
                                ),
                                _(
                                    'Al cargar la información, vas a acceder a un reporte detallado de la campaña con resultados clave para planificar la próxima.'
                                )
                            ),
                        },
                        {
                            label: _('Siembra tardía'),
                            title: pick(
                                _('Ayudá a los productores a estimar el rendimiento de su cosecha'),
                                _('Estimá el rendimiento de tu cosecha')
                            ),
                            description: pick(
                                _(
                                    'A través de la carga de muestras los productores pueden estimar el rendimiento y conocer la fecha probable de cosecha.'
                                ),
                                _(
                                    'A través de la carga de muestras podés estimar el rendimiento y conocer la fecha probable de cosecha.'
                                )
                            ),
                            actionText: _('Ver cómo calcular el rendimiento'),
                            action: () => {
                                setOpenYieldEstimationModal(true);
                            },
                        },
                    ],
                    fromMonth: 2, // Marzo
                    toMonth: 2, // Marzo
                },
                {
                    cards: [
                        {
                            label: '',
                            title: pick(
                                _('Ayudá a los productores a planificar su próxima campaña'),
                                _('Planificá tu próxima campaña')
                            ),
                            description: pick(
                                _('Recomendales el híbrido más adecuado para sus lotes.'),
                                _('Elegí el híbrido más adecuado para tus lotes.')
                            ),
                            actionText: _('Recomendar híbridos Dekalb'),
                            action: goToSeedRecommendation,
                            hidden: !cornRecommendationEnabled,
                        },
                    ],
                    fromMonth: 3, // Abril
                    toMonth: 3, // Abril
                },
                {
                    cards: [
                        {
                            label: '',
                            title: pick(
                                _('Asegurate de que los productores planifiquen el barbecho con el herbicida adecuado'),
                                _('Planificá el barbecho con el herbicida adecuado')
                            ),
                            description: _('Un control eficiente de malezas es clave para optimizar la siembra.'),
                            actionText: _('Recomendar herbicidas'),
                            action: () => goToCPRecommendation({ qs: { type: 'weeds' } }),
                            hidden: !cropProtectionEnabled,
                        },
                    ],
                    fromMonth: 4, // Mayo
                    toMonth: 6, // Julio
                },
                {
                    cards: [
                        {
                            label: _('Siembra Temprana'),
                            title: _('Recomendá la densidad de siembra óptima para el híbrido'),
                            description: pick(
                                _(
                                    'Ayudá a los productores a ajustar la densidad de siembra para maximizar el rendimiento de sus cultivo.'
                                ),
                                _('Ajustá la densidad de siembra para maximizar el rendimiento de tus cultivos.')
                            ),
                            actionText: _('Recomendar densidad de siembra'),
                            action: goToManagementRecommendation,
                        },
                        {
                            label: _('Siembra tardía'),
                            title: pick(
                                _('Asegurate de que los productores planifiquen el barbecho con el herbicida adecuado'),
                                _('Planificá el barbecho con el herbicida adecuado')
                            ),
                            description: _('Un control eficiente de malezas es clave para optimizar la siembra.'),
                            actionText: _('Recomendar herbicidas'),
                            action: () => goToCPRecommendation({ qs: { type: 'weeds' } }),
                            hidden: !cropProtectionEnabled,
                        },
                    ],
                    fromMonth: 7, // Agosto
                    toMonth: 7, // Agosto
                },
                {
                    cards: [
                        {
                            label: _('Siembra Temprana'),
                            title: pick(
                                _('Incentivá a tus productores a Agregar sus cultivos en {{appName}}', { appName }),
                                _('Agregá tus cultivos en {{appName}}', { appName })
                            ),
                            description: pick(
                                _(
                                    'Al registrar los cultivos, podrán hacer un seguimiento en cada etapa, acceder a recomendaciones, alertas y estrategias de manejo.'
                                ),
                                _(
                                    'Al registrar los cultivos, vas a poder hacer un seguimiento en cada etapa, acceder a recomendaciones, alertas y estrategias de manejo.'
                                )
                            ),
                        },
                        {
                            label: _('Siembra tardía'),
                            title: pick(
                                _('Asegurate de que los productores planifiquen el barbecho con el herbicida adecuado'),
                                _('Planificá el barbecho con el herbicida adecuado')
                            ),
                            description: _('Un control eficiente de malezas es clave para optimizar la siembra.'),
                            actionText: _('Recomendar herbicidas'),
                            action: () => goToCPRecommendation({ qs: { type: 'weeds' } }),
                            hidden: !cropProtectionEnabled,
                        },
                    ],
                    fromMonth: 8, // Septiembre
                    toMonth: 8, // Septiembre
                },
                {
                    cards: [
                        {
                            label: _('Siembra Temprana'),
                            title: pick(
                                _('Ayuda al productor a controlar las malezas en post emergencia'),
                                _('Controlá las malezas en post emergencia')
                            ),
                            description: _('El manejo de malezas es clave para evitar la competencia con el cultivo.'),
                            actionText: _('Recomendar fitosanitarios'),
                            action: () => goToCPRecommendation({ qs: { type: 'weeds' } }),
                            hidden: !cropProtectionEnabled,
                        },
                        {
                            label: _('Siembra tardía'),
                            title: pick(
                                _('Asegurate de que los productores planifiquen el barbecho con el herbicida adecuado'),
                                _('Planificá el barbecho con el herbicida adecuado')
                            ),
                            description: _('Un control eficiente de malezas es clave para optimizar la siembra.'),
                            actionText: _('Recomendar herbicidas'),
                            action: () => goToCPRecommendation({ qs: { type: 'weeds' } }),
                            hidden: !cropProtectionEnabled,
                        },
                    ],
                    fromMonth: 9, // Octubre
                    toMonth: 9, // Octubre
                },
                {
                    cards: [
                        {
                            label: _('Siembra Temprana'),
                            title: pick(
                                _('Recomendale a los productores optimizar la nutrición con N-Smart'),
                                _('Optimizá la nutrición de tus lotes con N-Smart')
                            ),
                            description: pick(
                                _(
                                    'Sugerile a los productores el uso de N-Smart para mejorar la disponibilidad de nitrógeno y potenciar el desarrollo del cultivo.'
                                ),
                                _(
                                    'Usá N-Smart para mejorar la disponibilidad de nitrógeno y potenciar el desarrollo del cultivo.'
                                )
                            ),
                        },
                        {
                            label: _('Siembra tardía'),
                            title: _('Recomendá la densidad de siembra para el híbrido'),
                            description: pick(
                                _(
                                    'Ayudá a los productores a ajustar la densidad de siembra para maximizar el rendimiento de sus cultivo.'
                                ),
                                _('Ajustá la densidad de siembra para maximizar el rendimiento de tus cultivos.')
                            ),
                            actionText: _('Recomendar densidad de siembra'),
                            action: goToManagementRecommendation,
                        },
                    ],
                    fromMonth: 10, // Noviembre
                    toMonth: 10, // Noviembre
                },
                {
                    cards: [
                        {
                            label: _('Siembra Temprana'),
                            title: pick(
                                _('Seguí de cerca el monitoreo de plagas y enfermedades de tus productores'),
                                _('Seguí de cerca el monitoreo de plagas y enfermedades de tus lotes')
                            ),
                            description: pick(
                                _(
                                    'Evaluá el estado del cultivo y recomendales los fungicidas e insecticidas adecuados.'
                                ),
                                _('Evaluá el estado del cultivo y elegí los fungicidas e insecticidas adecuados.')
                            ),
                            actionText: _('Recomendar fitosanitarios'),
                            action: () => goToCPRecommendation({}),
                            hidden: !cropProtectionEnabled,
                        },
                        {
                            label: _('Siembra tardía'),
                            title: pick(
                                _('Incentivá a tus productores a Agregar sus cultivos en {{appName}}', { appName }),
                                _('Agregá tus cultivos en {{appName}}', { appName })
                            ),
                            description: pick(
                                _(
                                    'Al registrar los cultivos, podrán hacer un seguimiento en cada etapa, acceder a recomendaciones, alertas y estrategias de manejo.'
                                ),
                                _(
                                    'Al registrar los cultivos, vas a poder hacer un seguimiento en cada etapa, acceder a recomendaciones, alertas y estrategias de manejo.'
                                )
                            ),
                        },
                    ],
                    fromMonth: 11, // Diciembre
                    toMonth: 11, // Diciembre
                },
            ].filter((suggestion) => suggestion.cards.some((card) => !card.hidden)),
        // `pick` carries the audience, so it must invalidate the schedule too.
        [i18n.resolvedLanguage, pick, cropProtectionEnabled, cornRecommendationEnabled]
    );

    const activeSuggestion = useMemo(() => {
        const currentMonth = new Date().getMonth();
        return suggestions.find(
            (suggestion) => currentMonth >= suggestion.fromMonth && currentMonth <= suggestion.toMonth
        );
    }, [suggestions]);

    const visibleCards = useMemo(
        () => activeSuggestion?.cards?.filter((card) => !card.hidden) ?? [],
        [activeSuggestion]
    );

    if (!visibleCards.length) {
        return null;
    }

    return (
        <Box
            sx={{
                border: (t) => `1px solid ${t.palette.divider}`,
                padding: '24px',
                backgroundColor: '#FFFFFF',
                flex: 1,
                width: '100%',
                minHeight: 0,
            }}
        >
            <Stack spacing={3} height='100%'>
                <Stack spacing={1}>
                    <Typography color='primary' variant='h5'>
                        {_('Guía de recomendaciones')}
                    </Typography>
                    <Typography color='primary.mui.inactive' variant='body2'>
                        {pick(
                            _(
                                'Sugerencias y acciones que te pueden ayudar a organizar tu trabajo con los productores.'
                            ),
                            _('Sugerencias y acciones que te pueden ayudar a organizar el trabajo en tus lotes.')
                        )}
                    </Typography>
                </Stack>
                <Stack spacing={2}>
                    {visibleCards.map((suggestion, index) => (
                        <ScheduleCard
                            // biome-ignore lint/suspicious/noArrayIndexKey: false positive
                            key={index}
                            label={suggestion.label}
                            title={suggestion.title}
                            description={suggestion.description}
                            actionText={suggestion.actionText}
                            actionOnClick={suggestion.action}
                        />
                    ))}
                </Stack>
            </Stack>
        </Box>
    );
}

export default SuggestionsSection;
